const CACHE_PREFIX = 'pp-gameapp-cache:v1:';
const DEFAULT_TTL_MS = 60 * 1000;

const memoryCache = new Map();
const inFlightRequests = new Map();

function normalizeKey(key) {
    if (Array.isArray(key)) {
        return key.map((part) => (part == null ? 'null' : String(part))).join('::');
    }

    return String(key);
}

function getStorageKey(key) {
    return `${CACHE_PREFIX}${key}`;
}

function canUseSessionStorage() {
    try {
        return typeof window !== 'undefined' && !!window.sessionStorage;
    } catch {
        return false;
    }
}

function isExpired(entry) {
    return !entry || typeof entry.expiresAt !== 'number' || entry.expiresAt <= Date.now();
}

function readSessionEntry(key) {
    if (!canUseSessionStorage()) return null;

    try {
        const raw = window.sessionStorage.getItem(getStorageKey(key));
        if (!raw) return null;

        const entry = JSON.parse(raw);
        if (isExpired(entry)) {
            window.sessionStorage.removeItem(getStorageKey(key));
            return null;
        }

        return entry;
    } catch {
        return null;
    }
}

function writeSessionEntry(key, entry) {
    if (!canUseSessionStorage()) return;

    try {
        window.sessionStorage.setItem(getStorageKey(key), JSON.stringify(entry));
    } catch {
        // ignore storage quota / serialization errors
    }
}

function readCacheEntry(cacheKey) {
    const memoryEntry = memoryCache.get(cacheKey);
    if (memoryEntry && !isExpired(memoryEntry)) {
        return memoryEntry;
    }

    if (memoryEntry) {
        memoryCache.delete(cacheKey);
    }

    const sessionEntry = readSessionEntry(cacheKey);
    if (sessionEntry) {
        memoryCache.set(cacheKey, sessionEntry);
        return sessionEntry;
    }

    return null;
}

function writeCacheEntry(cacheKey, value, ttlMs, persistToSession) {
    const safeTtlMs = Number.isFinite(ttlMs) ? Math.max(ttlMs, 1000) : DEFAULT_TTL_MS;
    const entry = {
        value,
        cachedAt: Date.now(),
        expiresAt: Date.now() + safeTtlMs,
    };

    memoryCache.set(cacheKey, entry);
    if (persistToSession) {
        writeSessionEntry(cacheKey, entry);
    }

    return entry;
}

function matchesCacheKey(cacheKey, matcher) {
    if (typeof matcher === 'function') {
        return matcher(cacheKey);
    }

    const normalizedMatcher = normalizeKey(matcher);
    return cacheKey === normalizedMatcher || cacheKey.startsWith(normalizedMatcher);
}

export function createRequestCacheKey(...parts) {
    return normalizeKey(parts.flat());
}

export function peekCachedValue(key) {
    const entry = readCacheEntry(normalizeKey(key));
    return entry ? entry.value : undefined;
}

export function hasFreshCachedValue(key) {
    return readCacheEntry(normalizeKey(key)) != null;
}

export function fetchWithCache({
    key,
    loader,
    ttlMs = DEFAULT_TTL_MS,
    forceRefresh = false,
    persistToSession = true,
}) {
    const cacheKey = normalizeKey(key);

    if (!forceRefresh) {
        const cachedEntry = readCacheEntry(cacheKey);
        if (cachedEntry) {
            return Promise.resolve(cachedEntry.value);
        }
    }

    if (inFlightRequests.has(cacheKey)) {
        return inFlightRequests.get(cacheKey);
    }

    const requestPromise = Promise.resolve()
        .then(() => loader())
        .then((value) => {
            writeCacheEntry(cacheKey, value, ttlMs, persistToSession);
            return value;
        })
        .finally(() => {
            inFlightRequests.delete(cacheKey);
        });

    inFlightRequests.set(cacheKey, requestPromise);
    return requestPromise;
}

export function prefetchWithCache(options) {
    return fetchWithCache(options).catch(() => undefined);
}

export function invalidateRequestCache(matchers) {
    const normalizedMatchers = Array.isArray(matchers) ? matchers : (matchers != null ? [matchers] : []);

    const shouldRemove =
        normalizedMatchers.length === 0
            ? () => true
            : (cacheKey) => normalizedMatchers.some((matcher) => matchesCacheKey(cacheKey, matcher));

    for (const cacheKey of Array.from(memoryCache.keys())) {
        if (shouldRemove(cacheKey)) {
            memoryCache.delete(cacheKey);
            inFlightRequests.delete(cacheKey);
        }
    }

    if (!canUseSessionStorage()) return;

    const storageKeysToRemove = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
        const storageKey = window.sessionStorage.key(index);
        if (!storageKey || !storageKey.startsWith(CACHE_PREFIX)) continue;

        const cacheKey = storageKey.slice(CACHE_PREFIX.length);
        if (shouldRemove(cacheKey)) {
            storageKeysToRemove.push(storageKey);
        }
    }

    storageKeysToRemove.forEach((storageKey) => {
        window.sessionStorage.removeItem(storageKey);
    });
}
